import type { UnknownAction } from '@reduxjs/toolkit'
import type { ComponentType } from 'react'

import type { ShellNotice, ShellPresence, UiModule } from '@krp/client-runtime'
import { type MyResource } from '@krp/platform-ui'
import { aqsiConnectionForm } from '@krp/resource-aqsi-ui'
import { onecConnectionForm } from '@krp/resource-onec-ui'
import { SYNC_WINDOWS } from '@krp/sync-ui'

import { AqsiSourceStripRow } from '../bindings/aqsi/AqsiSourceStripRow'
import { AqsiVatCard } from '../bindings/aqsi/AqsiVatCard'
import { useAttentionCounter } from '../bindings/aqsi/useAttentionCounter'
import { useParkedCounter } from '../bindings/aqsi/useParkedCounter'
import { OneCVatCard } from '../bindings/onec/OneCVatCard'
import type { NomenclatureCardProps } from '../bindings/platform/vatCardKit'
import { useSyncDivergenceCount } from '../bindings/sync/useSyncDivergenceCount'
import { MODULES } from './modules'

/**
 * Реестр вкладов в оболочку (RP-SEP5). Оболочка (`app`) и платформенные привязки (`bindings/platform`) вида ресурса не знают: они
 * читают этот реестр, а что в него попало, решают пакеты видов. Два источника вкладов:
 * - данные модуля (`UiModule.shell`: уведомления, присутствие) собираются из `MODULES` сами — вклад объявляет `module.ts` пакета;
 * - вклады с компонентами и запросами через store приложения (счётчики меню, полоса источников, карточки настроек номенклатуры)
 *   пакет объявить не может — store ему не виден (D-051): их связывает таблица ниже, по одной строке на вид.
 */

/** Счётчик у пункта меню: число и подпись, которую читает экранный диктор. */
export interface MenuCounter {
  count: number
  label: string
}

/**
 * Уведомления модуля с его ключом в RootState: оболочка читает срез `state[moduleId]`. Срезы модулей разных типов, поэтому реестр
 * объявляет `select` методом с параметром `unknown`: параметры методов сравниваются двунаправленно, и `select` модуля с его
 * срезом присваивается без приведения типов. Что подать в `select`, знает оболочка — срез именно этого модуля.
 */
export interface ModuleNotifications {
  moduleId: string
  notifications: {
    select(moduleState: unknown): Readonly<Record<string, ShellNotice>>
    clear(key: string): UnknownAction
  }
}

// Модули разных пакетов имеют разные срезы `S`; реестр хранит их под общим типом, срез отдаёт оболочка по `moduleId`.
type AnyModule = Pick<UiModule<never>, 'id' | 'shell'>

function collect(modules: readonly AnyModule[]) {
  const notifications: ModuleNotifications[] = []
  const presence: ShellPresence[] = []
  for (const module of modules) {
    if (module.shell?.notifications !== undefined) notifications.push({ moduleId: module.id, notifications: module.shell.notifications })
    if (module.shell?.presence !== undefined) presence.push(module.shell.presence)
  }
  return { notifications, presence }
}

const collected = collect(MODULES)

export const SHELL_NOTIFICATIONS: readonly ModuleNotifications[] = collected.notifications
export const SHELL_PRESENCE: readonly ShellPresence[] = collected.presence

/**
 * Хуки счётчиков пунктов меню (по id раздела; у пункта-окна вне разделов — по виду окна). Список постоянный на всё время жизни приложения, поэтому порядок вызова хуков стабилен.
 * Счётчики одного раздела от разных видов складываются в список, а не в сумму.
 */
export const MENU_COUNTER_HOOKS: readonly (() => Readonly<Record<string, readonly MenuCounter[]>>)[] = [
  // RP-FB35: «Чеки» → «требуют внимания» и «отложенные» (AQSI).
  function useAqsiMenuCounters(): Readonly<Record<string, readonly MenuCounter[]>> {
    const attention = useAttentionCounter()
    const parked = useParkedCounter()
    const receipts = [
      ...(attention > 0 ? [{ count: attention, label: 'Требуют внимания' }] : []),
      ...(parked > 0 ? [{ count: parked, label: 'Отложенные продажи' }] : []),
    ]
    return receipts.length > 0 ? { receipts } : {}
  },
  // RP-SYN8: «Синхронизация» — пункт-окно вне разделов, поэтому счётчик ключуется видом окна: сколько расхождений ждут решения.
  function useSyncMenuCounters(): Readonly<Record<string, readonly MenuCounter[]>> {
    const divergences = useSyncDivergenceCount()
    return divergences > 0 ? { [SYNC_WINDOWS.home]: [{ count: divergences, label: 'Расхождения ждут решения' }] } : {}
  },
]

/**
 * Полоса «Источники» над общим каталогом перечисляет ВСЕ источники из «моих ресурсов» (RP-SRC1, Е-4); здесь — вклады видов, которые
 * умеют показать свежесть каталога своей строкой. Вид без вклада виден простой строкой без проверки.
 */
export const SOURCES_STRIP_ROWS: Readonly<Record<string, ComponentType<{ resource: MyResource }>>> = {
  [aqsiConnectionForm.kind]: AqsiSourceStripRow,
}

/** Карточка ставки НДС источника в «Номенклатура → Настройка» по виду ресурса. Вида нет — источник в настройке не показывается. */
export const NOMENCLATURE_SETTINGS_CARDS: Readonly<Record<string, ComponentType<NomenclatureCardProps>>> = {
  [onecConnectionForm.kind]: OneCVatCard,
  [aqsiConnectionForm.kind]: AqsiVatCard,
}
